import React from 'react';
import classnames from 'classnames';
import RepProfilePageTabs from '$pages/$rep-profile/tabs';
import Loading from 'components/pure/utils/Loading';
import {RepProfileTab} from '@store/coaching-plans';
import {formatDuration} from 'utils/periodAsText';
import CoachingPageContainer from 'components/coaching/CoachingPageContainer';
import {loadUpcomingReviewData} from 'components/dashboard/UpcomingCoachingAgenda';
import {getStringQueryParam} from 'utils/getQueryParam';
import RepProfileSelector from './RepProfileSelector';
import RepProfileSummary from './RepProfileSummary';
import RepProfileCallActivities from './RepProfileCallActivities';
import RepProfileCoachingActivities from './RepProfileCoachingActivities';
import RepProfileCoachingReceivedActivities from './RepProfileCoachingReceivedActivities';
import RepProfileCalls from './RepProfileCalls';
import RepProfileSkills from './RepProfileSkills';
import {DashKpiPage} from '$pages/$rep-profile/kpi/DashKpiPage';
import './RepProfilePage.styl';
import {featureFlags} from 'utils/const';
import hasFlag from 'utils/hasFlag';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';

/**
 * Dates using non-standard (startDate,endDate] need to be corrected to [startDate + 1, endDate + 1)
 * before calling external actions or resources
 */
export default class RepProfilePage extends React.PureComponent {
  render() {
    const {
      LibraryStore,
      ManagerDashboardStore,
      RepProfilePageActions,
      RepProfilePageStore,
      SkillStore,
      TeamListStore,
      UserStore,
      AuthStore,
      CoachingReviewTypeStore,
      location,
    } = this.props;
    const {
      callActivity,
      callDonut,
      callTeamMetrics,
      teamConversationTrend,
      teamCallDurationTrend,
      coachingActivity,
      coachingTeamMetrics,
      coachingReceivedFrom,
      interchanges,
      interchangesLoading,
      talkListen,
      talkListenLoading,
      didCallActivityFetchFail,
      didCallDonutFetchFail,
      didCallTeamMetricsFail,
      didTeamConversationTrendFail,
      didTeamCallDurationTrendFail,
      didCoachingActivityFetchFail,
      didCoachingTeamMetricsFail,
      didCoachingPlansFetchFail,
      didSummaryFetchFail,
      isCallActivityFetching,
      isCallDonutFetching,
      isCallTeamMetricsFetching,
      isTeamConversationTrendFetching,
      isTeamCallDurationFetching,
      isCoachingActivityFetching,
      isCoachingTeamMetricsFetching,
      areCoachingPlansFetching,
      isSummaryFetching,
      openSummaries,
      selectedScoredByType,
      selectedTabIndex,
      summary,
    } = RepProfilePageStore;

    const {users} = UserStore;

    const repBeingViewed = UserStore.users.get(this.props.repId);

    // Adapt names to match conventions here.
    const didSkillsUserTrendFetchFail = SkillStore.wasErrorFetchingTeamTrends;

    const {managerInfo: managerInfoFromStore} = ManagerDashboardStore;
    const managerInfo = managerInfoFromStore
      ? managerInfoFromStore
      : this.props.managerId
      ? UserStore.users.get(this.props.managerId)
      : null;

    const {startDate, endDate, headerStartDate, headerEndDate} = this.props;
    const periodText = formatDuration(headerStartDate, headerEndDate, {
      shouldPluralize: false,
    });

    const onChangeCoachingDirection = direction => {
      const {users} = this.props.UserStore;
      const {teamId} = users.get(this.props.repId);

      RepProfilePageActions.getCoachingActivity({
        direction: direction,
        teamId: teamId,
        userId: this.props.repId,
        startDate,
        endDate,
      });
      RepProfilePageActions.getCoachingTeamMetrics({
        direction: direction,
        teamId: teamId,
        userId: this.props.repId,
        startDate,
        endDate,
      });
    };

    const sessionUser = AuthStore.user;
    const hasNoCallReviewType = CoachingReviewTypeStore.types.some(t => !t.includesCalls);
    const hasPlanId = getStringQueryParam(location, 'planId');

    return !repBeingViewed ? (
      <Loading />
    ) : (
      <div className="rep-profile-page">
        <div
          className={classnames(
            'rep-profile-page-layout',
            managerInfo || sessionUser.isManager() ? 'manager' : ''
          )}>
          <RepProfilePageTabs
            selectedTabIndex={selectedTabIndex}
            rep={repBeingViewed}
            onChangeCoachingDirection={onChangeCoachingDirection}
            direction={coachingReceivedFrom}
            onSelectTabIndex={this.props.onSelectTabIndex}
            flags={sessionUser.flags}
            startDate={startDate}
            endDate={endDate}
          />
          <div className="rep-profile-content">
            {didSummaryFetchFail ? (
              'There was a problem communicating with the server.'
            ) : (
              <div>
                <RepProfileSelector
                  startDate={headerStartDate}
                  endDate={headerEndDate}
                  onSelectRep={this.props.onSelectRep}
                  onSelectTeam={this.props.onSelectTeam}
                  teams={LibraryStore.shareAccessible.team.list}
                  reps={TeamListStore.users.get(this.props.teamId)}
                  repId={this.props.repId}
                  teamId={this.props.teamId}
                  sessionUser={sessionUser}
                  manager={managerInfo}
                  rep={repBeingViewed}
                  location={this.props.location}
                />
                {selectedTabIndex === RepProfileTab.SUMMARY && (
                  <div className="tab-content">
                    <RepProfileSummary
                      adHocNoCallOnly={true}
                      isCoach={UserStore.users.getIn([sessionUser.id, 'isCoach'])}
                      rep={repBeingViewed}
                      teammates={users.toList().filter(u => u.teamId === repBeingViewed.teamId)}
                      headerStartDate={headerStartDate}
                      headerEndDate={headerEndDate}
                      summary={summary}
                      interchanges={interchanges}
                      talkListen={talkListen}
                      onSelectRep={this.props.onSelectRep}
                      isSummaryLoading={isSummaryFetching}
                      interchangesLoading={interchangesLoading}
                      talkListenLoading={talkListenLoading}
                      didCallActivityFetchFail={didCallActivityFetchFail}
                      hasNoCallReviewType={hasNoCallReviewType}
                      coachId={
                        sessionUser.is_coach && !sessionUser.isManager() ? sessionUser.id : null
                      }
                      managerId={managerInfo ? managerInfo.id : null}
                      adminId={sessionUser.isAdmin() && sessionUser.id}
                    />
                    {/** TODO loading/ failure for the rest **/}
                  </div>
                )}
                {selectedTabIndex === RepProfileTab.CALL_ACTIVITY && (
                  <div className="tab-content">
                    {didCallActivityFetchFail || didCallDonutFetchFail || didCallTeamMetricsFail ? (
                      <p>There was a problem fetching the call activity.</p>
                    ) : (
                      <RepProfileCallActivities
                        rep={repBeingViewed}
                        callActivity={callActivity}
                        callDonut={callDonut}
                        callTeamMetrics={callTeamMetrics}
                        isCallActivityLoading={isCallActivityFetching}
                        isCallDonutLoading={isCallDonutFetching}
                        isCallTeamMetricsLoading={isCallTeamMetricsFetching}
                        onSelectRep={this.props.onSelectRep}
                        periodText={periodText}
                        startDate={startDate}
                        endDate={endDate}
                        headerStartDate={headerStartDate}
                        headerEndDate={headerEndDate}
                        location={this.props.location}
                        renderTeamAverage={true}
                        users={users}
                      />
                    )}
                  </div>
                )}
                {selectedTabIndex === RepProfileTab.REVIEW_ACTIVITIES && (
                  <div className="tab-content">
                    {didCoachingActivityFetchFail || didCoachingTeamMetricsFail ? (
                      <p>There was a problem fetching the coaching activity.</p>
                    ) : (
                      <RepProfileCoachingActivities
                        rep={repBeingViewed}
                        coachingActivity={coachingActivity}
                        coachingTeamMetrics={coachingTeamMetrics}
                        isCoachingActivityLoading={isCoachingActivityFetching}
                        isCoachingTeamMetricsLoading={isCoachingTeamMetricsFetching}
                        reviewType={coachingReceivedFrom}
                        periodText={periodText}
                        startDate={startDate}
                        endDate={endDate}
                        headerStartDate={headerStartDate}
                        headerEndDate={headerEndDate}
                        location={this.props.location}
                        renderTeamAverage={true}
                      />
                    )}
                  </div>
                )}
                {selectedTabIndex === RepProfileTab.COACHING_PLANS && (
                  <div className="tab-content">
                    {!hasPlanId &&
                      (didSkillsUserTrendFetchFail || didCoachingPlansFetchFail ? (
                        <p>There was a problem communicating with the server.</p>
                      ) : (
                        <RepProfileSkills
                          rep={repBeingViewed}
                          onSelectScoredByType={this.props.onSelectScoredByType}
                          selectedScoredByType={selectedScoredByType}
                          SkillStore={SkillStore}
                          areCoachingPlansLoading={areCoachingPlansFetching}
                          coachingPlans={RepProfilePageStore.coachingPlans}
                          headerStartDate={headerStartDate}
                          headerEndDate={headerEndDate}
                          sessionUser={this.props.AuthStore.user}
                          location={this.props.location}
                          teamId={this.props.teamId}
                        />
                      ))}
                    {hasPlanId && (
                      <CoachingPageContainer
                        {...this.props}
                        refreshUpcomingReviews={() =>
                          loadUpcomingReviewData(
                            this.props.managerId,
                            this.props.coachId,
                            UPCOMING_REVIEW_ROLES.PARTICIPANT
                          )
                        }
                      />
                    )}
                  </div>
                )}
                {selectedTabIndex === RepProfileTab.COACHING_RECEIVED && (
                  <div className="tab-content">
                    {didCoachingActivityFetchFail ? (
                      <p>There was a problem fetching the coaching activity.</p>
                    ) : (
                      <RepProfileCoachingReceivedActivities
                        rep={repBeingViewed}
                        coachingActivity={coachingActivity}
                        coachingTeamMetrics={coachingTeamMetrics}
                        isCoachingActivityLoading={isCoachingActivityFetching}
                        isCoachingTeamMetricsLoading={isCoachingTeamMetricsFetching}
                        coachingReceivedFrom={coachingReceivedFrom}
                        periodText={periodText}
                        startDate={startDate}
                        endDate={endDate}
                        headerStartDate={headerStartDate}
                        headerEndDate={headerEndDate}
                        location={this.props.location}
                        renderTeamAverage={true}
                      />
                    )}
                  </div>
                )}
                {hasFlag(featureFlags.adminKpis) && selectedTabIndex === RepProfileTab.KPI && (
                  <DashKpiPage
                    startDate={startDate}
                    endDate={endDate}
                    targetUser={repBeingViewed}
                  />
                )}
                {selectedTabIndex === RepProfileTab.CALLS && (
                  <div className="tab-content">
                    {didTeamCallDurationTrendFail || didTeamConversationTrendFail ? (
                      <p>There was a problem fetching the coaching activity.</p>
                    ) : (
                      <RepProfileCalls
                        teamConversationTrend={teamConversationTrend}
                        teamCallDurationTrend={teamCallDurationTrend}
                        rep={repBeingViewed}
                        openCallDetails={RepProfilePageActions.openCallDetails}
                        openSummaries={openSummaries}
                        callDetails={RepProfilePageStore.callDetails}
                        callDetailsLoading={RepProfilePageStore.callDetailsLoading}
                        callDetailsFailed={RepProfilePageStore.callDetailsFailed}
                        getCallDetails={RepProfilePageActions.getCallDetails}
                        getInfoAboutFiles={this.props.getInfoAboutFiles}
                        isLoading={isTeamCallDurationFetching || isTeamConversationTrendFetching}
                        getCoachingActivity={RepProfilePageActions.getCoachingActivity}
                        onToggleShowWeekends={this.props.onToggleShowWeekends}
                        showWeekends={this.props.showWeekends}
                        startDate={startDate}
                        endDate={endDate}
                        headerStartDate={headerStartDate}
                        headerEndDate={headerEndDate}
                        location={this.props.location}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
